/**
 * Estado del WebSocket: status de conexión + typing por room + emisor de typing
 * (función inyectada por useWS para que MessageInput pueda mandar typing events
 * sin tener que importar PartySocket ni el handler).
 */
import { create } from "zustand";

export type WSStatus = "idle" | "connecting" | "connected" | "reconnecting" | "closed";

type TypingState = { userId: string; until: number };

type WSState = {
  status: WSStatus;
  lastConnectedAt: number | null;
  /** Map<roomId, TypingState[]> — quién está escribiendo en cada room, con
   *  timestamp de expiración. Se limpia cada 4 s con un setInterval centralizado. */
  typingByRoom: Record<string, TypingState[]>;
  /** Función inyectada por useWS para enviar typing al server. */
  sendTyping: ((roomId: string) => void) | null;
  setStatus: (s: WSStatus) => void;
  setTyping: (roomId: string, userId: string) => void;
  prune: () => void;
  setSendTyping: (fn: ((roomId: string) => void) | null) => void;
};

export const useWSStore = create<WSState>((set, get) => ({
  status: "idle",
  lastConnectedAt: null,
  typingByRoom: {},
  sendTyping: null,
  setStatus: (s) =>
    set({
      status: s,
      lastConnectedAt: s === "connected" ? Date.now() : undefined,
    }),
  setTyping: (roomId, userId) => {
    const now = Date.now();
    const until = now + 4000;
    const list = (get().typingByRoom[roomId] ?? []).filter((t) => t.userId !== userId && t.until > now);
    list.push({ userId, until });
    set({ typingByRoom: { ...get().typingByRoom, [roomId]: list } });
  },
  prune: () => {
    const now = Date.now();
    const cleaned: Record<string, TypingState[]> = {};
    for (const [k, arr] of Object.entries(get().typingByRoom)) {
      const filtered = arr.filter((t) => t.until > now);
      if (filtered.length) cleaned[k] = filtered;
    }
    set({ typingByRoom: cleaned });
  },
  setSendTyping: (fn) => set({ sendTyping: fn }),
}));

// Limpieza periódica para que el "X está escribiendo" se borre solo.
if (typeof window !== "undefined") {
  setInterval(() => useWSStore.getState().prune(), 1000);
}
